// Copyright (c) Docugami, Inc. All rights reserved.

import { useEffect, useMemo, useState } from "react"
import { BucketTimer } from "../utils/bucketTimer";
import { DgWordCanvasProxy } from "../components/doc_hosting/doc_hosts/word/v3/DgWordCanvasProxy";
import { DgmlInstancesSingleton, useDocumentDgml } from "./documentDgml";
import { Logger, LoggerCategory } from "utils/Logger";
import { RequestState, useAsyncHelper } from "./AsyncHelper";
import { Config } from "config";
import axios from "axios";
import { useGetDgmlQuery } from "store/dgml/dgmlSlice";
import { createFolderId } from "components/file_management/file_helpers";
import { skipToken } from "@reduxjs/toolkit/dist/query/react";

class AaDgWordCanvasProxyInitializer {
    public static instance = new AaDgWordCanvasProxyInitializer();
    private _initializedPromise: Promise<DgWordCanvasProxy> | null = null;
    private _documentId: string = '';

    public async ensureInitialized(documentId: string, dgmlString: string): Promise<DgWordCanvasProxy> {
        if (!!this._documentId && documentId !== this._documentId) {
            throw new Error("Current design only allows a single document to be loaded for the lifetime of the addin");
        }

        this._documentId = documentId;

        if (!this._initializedPromise) {
            this._initializedPromise = this.initialize(dgmlString);
        }

        return await this._initializedPromise;
    }

    private async initialize(dgmlString: string): Promise<DgWordCanvasProxy> {
        try {
            BucketTimer.init();
            BucketTimer.depthToCollect = 1;
            BucketTimer.bucket(1, "010-WEB-Downloading-Dgml");
            if (!DgmlInstancesSingleton.isInitialized(this._documentId)) {
                throw new Error("Dgml is not ready");
            }

            const response = await axios.get(`${Config.reportsApiUrl}/files/${encodeURIComponent(this._documentId)}/aa-fields`);
            const fields = response.data;
            Logger.LogInformation(LoggerCategory.AssistedAuthoring, `init dgWordCanvas with ${fields}`);
            BucketTimer.bucket(1, "020-new-DgWordCanvas");
            const dgWordCanvasProxy = new DgWordCanvasProxy();
            await dgWordCanvasProxy.initialize(dgmlString, fields);
            Logger.LogInformation(LoggerCategory.AssistedAuthoring, "establishDgmlCorrelation");
            BucketTimer.bucket(1, "100-est");
            await dgWordCanvasProxy.establishDgmlCorrelation();
            BucketTimer.bucket(1, "700-createCC");
            await dgWordCanvasProxy.createContentControlsForFields();
            BucketTimer.bucket(1, "799-end");
            BucketTimer.dumpToConsole();
            return dgWordCanvasProxy;
        } catch (error) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, error);
            throw error;
        }
    }
}

/**
 * Hook to get the DgWordCanvas for this document once it is ready
 */
export function useAaDgWordCanvasProxy(documentId: string): DgWordCanvasProxy | null {
    const [curDocumentId, setCurDocumentId] = useState('');
    const { execute, state, data } = useAsyncHelper<DgWordCanvasProxy>("Intitialized DgWordCanvasProxy", documentId, () => setCurDocumentId(documentId));

    const folderId = useMemo(() => !!documentId ? createFolderId(documentId) : undefined, [documentId]);
    const dgml = useDocumentDgml(documentId);
    const { data: dgmlFileData } = useGetDgmlQuery(!!folderId ? { docsetId: folderId.clusterId, fileId: folderId.fileId, includeText: true } : skipToken)

    useEffect(() => {
        if (!documentId) {
            setCurDocumentId('');
            return;
        }

        if (curDocumentId === documentId) {
            return;
        }

        if (!dgml.isInitialized()) {
            return;
        }

        if (!dgmlFileData?.value) {
            return;
        }

        if (state === RequestState.InProgress || state === RequestState.Error) {
            return;
        }

        execute(AaDgWordCanvasProxyInitializer.instance.ensureInitialized(documentId, dgmlFileData.value));
    },
    [documentId, curDocumentId, state, execute, data, dgml, dgmlFileData?.value])

    return data ?? null;
}
